import { TodoStatus } from './flora-todo-bar.type';
import type { Message, ToolCall } from '../../flora.type';
import type { AgentTodo, TodoBarState, TodoSummary } from './flora-todo-bar.type';

/** The langchain `TodoListMiddleware` tool name; each call replaces the whole list. */
/* grammar-check disable-next-line */
const TODO_TOOL_NAME = 'write_todos';

const VALID_STATUSES: readonly string[] = Object.values(TodoStatus);

/**
 * Narrow an unknown value to a well-formed, non-empty `AgentTodo[]`.
 *
 * Returns `null` for anything that is not an array of `{ content, status }`
 * objects with a recognized status — a partial or malformed `write_todos`
 * payload must never render a broken bar.
 *
 * @param {unknown} value - Candidate `todos` value from a tool call's input.
 * @returns {AgentTodo[] | null} The validated todos, or null when unusable.
 */
function parseTodos(value: unknown): AgentTodo[] | null {
  if (!Array.isArray(value) || value.length === 0) {
    return null;
  }
  const todos: AgentTodo[] = [];
  for (const item of value) {
    if (typeof item !== 'object' || item === null) {
      return null;
    }
    const { content, status } = item as { content?: unknown; status?: unknown };
    if (typeof content !== 'string' || typeof status !== 'string' || !VALID_STATUSES.includes(status)) {
      return null;
    }
    todos.push({ content, status: status as TodoStatus });
  }
  return todos;
}

/**
 * Extract the todos a single `write_todos` tool call carries.
 *
 * Prefers the settled `tool_input.todos`, falling back to a best-effort parse
 * of the still-streaming `partial_input` JSON so the very first list appears
 * before its arguments have fully arrived.
 *
 * @param {ToolCall} toolCall - A tool call to read todos from.
 * @returns {AgentTodo[] | null} The call's todos, or null when it has none yet.
 */
function todosFromToolCall(toolCall: ToolCall): AgentTodo[] | null {
  if (toolCall.tool_name !== TODO_TOOL_NAME) {
    return null;
  }
  const settled = parseTodos(toolCall.tool_input?.todos);
  if (settled) {
    return settled;
  }
  // Mid-stream: arguments live in `partial_input` until the call settles.
  if (toolCall.partial_input) {
    try {
      const parsed: unknown = JSON.parse(toolCall.partial_input);
      if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parseTodos((parsed as { todos?: unknown }).todos);
      }
    } catch {
      // Incomplete JSON fragment — nothing usable yet.
    }
  }
  return null;
}

/**
 * Extract the most recent `write_todos` payload from a message's persisted
 * content blocks (the history/reload shape).
 *
 * A reloaded thread spreads backend messages verbatim, storing tool calls in
 * `content: ContentBlock[]` as `{ type: 'tool_call', name, input }` — a
 * different field and shape than the live socket's `toolCalls[]`. Scanning
 * newest-first keeps the last-write-wins contract on history loads too.
 *
 * @param {Message['content']} content - A message's content (string or blocks).
 * @returns {AgentTodo[] | null} The latest persisted todos, or null when none.
 */
function todosFromContentBlocks(content: Message['content']): AgentTodo[] | null {
  if (!Array.isArray(content)) {
    return null;
  }
  for (let index = content.length - 1; index >= 0; index -= 1) {
    const block = content[index];
    if (block.type === 'tool_call' && block.name === TODO_TOOL_NAME) {
      const todos = parseTodos((block.input as { todos?: unknown })?.todos);
      if (todos) {
        return todos;
      }
    }
  }
  return null;
}

/**
 * Extract the most recent `write_todos` payload from a single message.
 *
 * Reads the live `toolCalls[]` (streaming) first, then falls back to the
 * persisted `content` blocks (history/reload). Each `write_todos` call replaces
 * the whole list, so the last one found wins.
 *
 * @param {Message | undefined} message - The message to read todos from.
 * @returns {AgentTodo[] | null} The message's latest todos, or null when none.
 */
function todosFromMessage(message: Message | undefined): AgentTodo[] | null {
  if (!message) {
    return null;
  }
  const toolCalls = message.toolCalls ?? [];
  for (let index = toolCalls.length - 1; index >= 0; index -= 1) {
    const todos = todosFromToolCall(toolCalls[index]);
    if (todos) {
      return todos;
    }
  }
  return todosFromContentBlocks(message.content);
}

/**
 * Resolve the todo list to show above the message bar, noting whether the
 * current turn wrote it.
 *
 * Responsibilities:
 * 1. Prefer the current turn's todos (the assistant message whose `turnId`
 *    matches the trailing question), so a live turn updates in place — falling
 *    back to the last message when no `turnId` exists (history/fixtures).
 * 2. When the current turn has emitted no todos yet, fall back to the most
 *    recent todos anywhere in the conversation, but keep them only while some
 *    remain uncompleted — so an in-flight plan is never removed from render by a
 *    new turn starting, while a fully completed plan is dropped once superseded.
 *
 * @param {Message[]} messages - Assistant messages, one per turn, oldest first.
 * @param {Message[]} questions - User questions, one per turn, oldest first.
 * @returns {TodoBarState} The todos to render (null when none apply) and
 *   whether they belong to the current turn.
 */
function deriveTodoBarState(messages: Message[], questions: Message[]): TodoBarState {
  if (messages.length === 0) {
    return { todos: null, isCurrentTurn: false };
  }

  const latestTurnId = questions[questions.length - 1]?.turnId;

  // 1. The current turn's todos take precedence (live updates, then persist for
  //    the rest of that turn even once everything is completed).
  if (latestTurnId !== undefined) {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      if (messages[index]?.turnId === latestTurnId) {
        const currentTodos = todosFromMessage(messages[index]);
        if (currentTodos) {
          return { todos: currentTodos, isCurrentTurn: true };
        }
        break;
      }
    }
  } else {
    const currentTodos = todosFromMessage(messages[messages.length - 1]);
    if (currentTodos) {
      return { todos: currentTodos, isCurrentTurn: true };
    }
  }

  // 2. The current turn has no todos yet. Keep the most recent prior plan on
  //    screen only while it still has unfinished work — a new turn must not
  //    remove an incomplete todo list, but a finished one should fall away.
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const todos = todosFromMessage(messages[index]);
    if (todos) {
      return { todos: todos.some((todo) => todo.status !== TodoStatus.COMPLETED) ? todos : null, isCurrentTurn: false };
    }
  }
  return { todos: null, isCurrentTurn: false };
}

/**
 * Resolve the todo list to show above the message bar (see `deriveTodoBarState`).
 * Convenience accessor for callers that only need the todos, not the turn flag.
 *
 * @param {Message[]} messages - Assistant messages, one per turn, oldest first.
 * @param {Message[]} questions - User questions, one per turn, oldest first.
 * @returns {AgentTodo[] | null} The todos to render, or null when none apply.
 */
function deriveCurrentTodos(messages: Message[], questions: Message[]): AgentTodo[] | null {
  return deriveTodoBarState(messages, questions).todos;
}

/**
 * Summarize a todo list into counts used by the collapsed bar header.
 *
 * @param {AgentTodo[]} todos - The todos to summarize.
 * @returns {TodoSummary} Total count, completed count, and the label of the first in-progress todo
 *   (or the first pending one when nothing is in progress), else null.
 */
function summarizeTodos(todos: AgentTodo[]): TodoSummary {
  const total = todos.length;
  const completed = todos.filter((todo) => todo.status === TodoStatus.COMPLETED).length;
  const active =
    todos.find((todo) => todo.status === TodoStatus.IN_PROGRESS) ??
    todos.find((todo) => todo.status === TodoStatus.PENDING);
  return { total, completed, activeLabel: active?.content ?? null };
}

export { deriveCurrentTodos, deriveTodoBarState, parseTodos, summarizeTodos };
